import { Suspense, useMemo, type ReactNode } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { Region, type ComponentType } from '@/components/region';
import {
    InsideSpace as InsideSpaceContainer,
    type InsideSpaceLogoSize,
    type InsideSpaceProps as InsideSpaceContainerProps,
} from '@/features/inside-space/inside-space';
/** @sfdc-extension-block-start SNK */
import {
    getInsideSpaceCardsLayoutClassName,
    type InsideSpaceCardsLayout,
} from '@/features/inside-space/inside-space-layout';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
/** @sfdc-extension-block-end SNK */
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';

export interface InsideSpaceProps extends Omit<InsideSpaceContainerProps, 'cards'> {
    /** BM metadata only; does not change runtime behaviour. Used for Dockyard translation workflows. */
    sendForTranslation?: boolean;
    /**
     * Optional card area (e.g. Storybook). Used when `component` is not set.
     * Not a Page Designer attribute.
     */
    previewCards?: ReactNode;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('insideSpace', {
    name: 'Inside Space',
    description:
        'Editorial section with optional logo and footer CTA. Add Link Card components to the cards region; enable "Inside Space layout" on each card for typography that matches the design.',
})
@RegionDefinition([
    {
        id: 'cards',
        name: 'Link cards',
        description: 'Add one or more Link Card components (set Inside Space layout on each card as needed).',
        componentTypeInclusions: ['linkCard'],
    },
])
export class InsideSpaceMetadata {
    @AttributeDefinition({
        id: 'showDefaultLogo',
        name: 'Show default logo',
        description: 'When enabled, shows the Inside Space wordmark above the card grid.',
        type: 'boolean',
        required: false,
        defaultValue: false,
    })
    showDefaultLogo?: boolean;

    /** @sfdc-extension-block-start SNK */
    @AttributeDefinition({
        id: 'logoSize',
        name: 'Logo size preset',
        description: 'Width preset for the default wordmark when Logo size (px) is not set.',
        type: 'enum',
        required: false,
        values: ['compact', 'default', 'large'],
        defaultValue: 'default',
    })
    logoSize?: InsideSpaceLogoSize;

    @AttributeDefinition({
        id: 'logoSizePx',
        name: 'Logo size (px)',
        description: 'Logo width in pixels (overrides the preset). Valid range: 80–400.',
        type: 'integer',
        required: false,
    })
    logoSizePx?: number;

    @AttributeDefinition({
        id: 'paddingPx',
        name: 'Padding (px)',
        description: 'Uniform section padding in pixels (same on mobile and desktop). Valid range: 0–80.',
        type: 'integer',
        required: false,
    })
    paddingPx?: number;

    @AttributeDefinition({
        id: 'cardsLayout',
        name: 'Cards layout',
        description: 'Stack link cards vertically (recommended in the burger menu) or use a responsive grid.',
        type: 'enum',
        required: false,
        values: ['grid', 'stack'],
        defaultValue: 'stack',
    })
    cardsLayout?: InsideSpaceCardsLayout;
    /** @sfdc-extension-block-end SNK */

    @AttributeDefinition({
        id: 'ctaLabel',
        name: 'CTA label',
        description: 'Optional footer link text (e.g. Discover more). Requires CTA destination.',
        type: 'string',
        required: false,
    })
    ctaLabel?: string;

    @AttributeDefinition({
        id: 'ctaTo',
        name: 'CTA destination',
        description: 'Optional footer link URL. Requires CTA label.',
        type: 'url',
        required: false,
    })
    ctaTo?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background hex colour',
        description: 'Hex background for the section (e.g. #f6f2ed). Invalid hex falls back to the default cream.',
        type: 'string',
        required: false,
        defaultValue: '',
    })
    backgroundColor?: string;

    @AttributeDefinition({
        id: 'sendForTranslation',
        name: 'Send for translation',
        description: 'Mark for translation workflows in Business Manager. Does not affect storefront rendering.',
        type: 'boolean',
        required: false,
        defaultValue: false,
    })
    sendForTranslation?: boolean;
}
/* v8 ignore stop */

export default function InsideSpace({
    previewCards,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    sendForTranslation: _sendForTranslation,
    /** @sfdc-extension-line SNK */
    cardsLayout = 'stack',
    ...containerProps
}: InsideSpaceProps) {
    /** @sfdc-extension-block-start SNK */
    const cardsLayoutClassName = getInsideSpaceCardsLayoutClassName(cardsLayout);

    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, InsideSpaceMetadata) : undefined),
        [component]
    );

    const cards: ReactNode = componentWithRegionMetadata ? (
        <Suspense fallback={null}>
            <Region
                component={componentWithRegionMetadata}
                regionId="cards"
                errorElement={null}
                className={cardsLayoutClassName}
            />
        </Suspense>
    ) : previewCards ? (
        <div className={cardsLayoutClassName}>{previewCards}</div>
    ) : null;
    /** @sfdc-extension-block-end SNK */

    return <InsideSpaceContainer {...containerProps} cards={cards} />;
}

// eslint-disable-next-line react-refresh/only-export-components
export { InsideSpacePdSkeleton, InsideSpacePdSkeleton as fallback } from './skeleton';
